import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getStudentAttemptAnalytics } from '../../services/teacherService';
import { StudentAttemptAnalytics } from '../../types/teacher';

interface StudentAttemptMetricsProps {
  studentId: number;
  studentName: string;
  refreshTrigger?: number;
}

const StudentAttemptMetrics: React.FC<StudentAttemptMetricsProps> = ({
  studentId,
  studentName,
  refreshTrigger
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<StudentAttemptAnalytics | null>(null);

  const fetchAttemptAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStudentAttemptAnalytics(studentId);
      setAnalytics(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load attempt analytics';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchAttemptAnalytics();
  }, [refreshTrigger, fetchAttemptAnalytics]);

  const getEfficiencyColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getEfficiencyLabel = (score: number): string => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Improvement';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Attempt Analytics"
        subheader={`How efficiently ${studentName} solves problems`}
        avatar={<SpeedIcon color="primary" />}
        action={
          <Tooltip title="Points are calculated as 1 ÷ attempts. First-try = 1.0 points, second-try = 0.5 points, etc.">
            <InfoIcon color="action" sx={{ cursor: 'help' }} />
          </Tooltip>
        }
      />
      <Divider />
      <CardContent>
        {analytics.totalProblemsSolved === 0 ? (
          <Typography color="text.secondary">
            No problems solved yet. Analytics will appear once the student starts solving problems.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {/* Average Attempts */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Average Attempts
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {analytics.averageAttempts}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  per problem solved
                </Typography>
              </Box>
            </Grid>

            {/* First-Try Success Rate */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  First-Try Success
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h4" fontWeight="bold">
                    {analytics.firstTrySuccessRate}%
                  </Typography>
                  {analytics.firstTrySuccessRate >= 50 && (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  solved on first attempt
                </Typography>
              </Box>
            </Grid>

            {/* Efficiency Score */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Efficiency Score
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h4" fontWeight="bold">
                    {analytics.efficiencyScore}%
                  </Typography>
                  <TrendingUpIcon
                    color={getEfficiencyColor(analytics.efficiencyScore)}
                    fontSize="small"
                  />
                </Box>
                <Chip
                  label={getEfficiencyLabel(analytics.efficiencyScore)}
                  color={getEfficiencyColor(analytics.efficiencyScore)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>

            {/* Total Problems Solved */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Problems Solved
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {analytics.totalProblemsSolved}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  in {analytics.totalAttempts} total attempts
                </Typography>
              </Box>
            </Grid>

            {/* Explanation */}
            <Grid item xs={12}>
              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2">
                  <strong>How Points Work:</strong> Students earn points based on how many
                  attempts they need. 1st attempt = 1.0 points, 2nd attempt = 0.5 points, 3rd
                  attempt = 0.33 points, and so on. The efficiency score shows how close they
                  are to solving every problem on the first try (100% = perfect).
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default StudentAttemptMetrics;
